import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as AudioAiIcon } from "../../../Assets/icon/Audio-ai.svg";
import { ReactComponent as XGreyIcon } from "../../../Assets/icon/X-grey.svg";
import { markElementViewed } from "../../../Services/elementsOverview";
import { getShowingUpRecently, getTryDifferentApproach } from "../../../Services/elements";
import { enterElementConversation } from "../../../Services/discovery";
import { initiateChatSession } from "../../../Services/Chat";
import { tk, MODE_DESCRIPTION } from "./copy";
import "./style.css";

// Map an element's domain key to the API spec key (Judgement → Judgment, Motivation → Motives).
const DOMAIN_KEY_NORMALIZE = {
  Identity: "Identity",
  Cognition: "Cognition",
  Judgement: "Judgment",
  Judgment: "Judgment",
  Resolution: "Resolution",
  Motivation: "Motives",
};

// `content.levelDescription` arrives as one HTML blob with the lead paragraph,
// then `<b>Strength:</b> ...`, then `<b>Potential risk:</b> ...` (sometimes
// "Potential Risk" with a capital R). Split on the bold labels so the modal
// can show three discrete sections that match the Figma.
const parseLevelDescription = (raw) => {
  if (!raw || typeof raw !== "string") return { howThisShowsUp: "", strength: "", watchFor: "" };

  const html = raw
    .replace(/<br\s*\/?>(\s*<br\s*\/?>)*/gi, "<br/>") // normalize <br>
    .replace(/&nbsp;/g, " ");

  // Split on the bold-label boundaries. The regex captures the label name
  // so we can route each segment into the right bucket.
  const parts = html.split(/<b>\s*(Strength|Potential\s*Risk|Potential\s*risk)\s*:?\s*<\/b>/i);

  // parts[0] is the lead paragraph. After that the array alternates:
  // [label, segment, label, segment, ...]
  const sections = { howThisShowsUp: (parts[0] || "").trim(), strength: "", watchFor: "" };

  for (let i = 1; i < parts.length; i += 2) {
    const label = (parts[i] || "").toLowerCase();
    const body = (parts[i + 1] || "").trim();
    if (label.startsWith("strength")) sections.strength = body;
    else if (label.startsWith("potential")) sections.watchFor = body;
  }

  // Trim trailing/leading <br/> noise from each section.
  const cleanup = (s) => s.replace(/^(<br\/>\s*)+/i, "").replace(/(\s*<br\/>)+$/i, "").trim();
  sections.howThisShowsUp = cleanup(sections.howThisShowsUp);
  sections.strength = cleanup(sections.strength);
  sections.watchFor = cleanup(sections.watchFor);
  return sections;
};

// The Recent / Grow card content arrives as a single plain-text blob with
// blank-line-separated sections. Each section's first line is the section
// title, and the body may contain an "intro" line ending in `:`, dash-prefixed
// bullet items, and/or paragraph lines. Convert that into structured sections
// so we can render the multi-block layout from Figma node 14914:17519
// (centered titles, gray intros, plain centered text lines).
const parseCardContent = (raw) => {
  if (!raw || typeof raw !== "string") return [];

  const blocks = raw.split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);

  return blocks
    .map((block) => {
      const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
      if (lines.length === 0) return null;

      const section = { title: lines[0], items: [] };
      let i = 1;
      while (i < lines.length) {
        const line = lines[i];
        if (/^[-*•]\s*/.test(line)) {
          // Group consecutive bullet lines into a single list block.
          const bullets = [];
          while (i < lines.length && /^[-*•]\s*/.test(lines[i])) {
            bullets.push(lines[i].replace(/^[-*•]\s*/, ""));
            i++;
          }
          section.items.push({ kind: "list", lines: bullets });
        } else if (line.endsWith(":") && section.items.length === 0) {
          // Only the first ":-terminated" line is treated as the gray intro
          // (matches the Figma where a single intro precedes the bullet list).
          section.items.push({ kind: "intro", text: line });
          i++;
        } else {
          section.items.push({ kind: "para", text: line });
          i++;
        }
      }
      return section;
    })
    .filter(Boolean);
};

// Pull the current coaching mode the user is in. Defaults to "keyboard" so that
// a freshly-installed account lands in chat instead of dialing voice.
// Still used by the roleplay CTA and by the fallback path when the Element
// conversation-entry endpoint cannot be reached; the "Talk with Aris" CTA no
// longer reads it, because how to continue is the member's explicit choice.
const getCoachingMode = () => {
  try {
    const v = (localStorage.getItem("coaching_mode") || "").trim().toLowerCase();
    return v === "voice" ? "voice" : "keyboard";
  } catch (_) {
    return "keyboard";
  }
};

/**
 * The member's last Element-conversation mode, mirrored locally.
 *
 * `remember_mode: true` persists the choice server-side as
 * `preferences.preferred_modality`, but the entry endpoint deliberately asks
 * every time (`ElementConversationService.enter` returns the mode choice
 * whenever `mode is None`) and never echoes the stored value back. The spec
 * calls the preference "a soft default, always easy to switch", so the local
 * mirror is what makes the remembered option *pre-selected* rather than
 * skipped — it never bypasses the question.
 */
const REMEMBERED_MODE_KEY = "element_talk_mode";

const getRememberedMode = () => {
  try {
    const v = (localStorage.getItem(REMEMBERED_MODE_KEY) || "").toUpperCase();
    return v === "VOICE" || v === "TEXT" ? v : null;
  } catch (_) {
    return null;
  }
};

const rememberMode = (mode) => {
  try {
    localStorage.setItem(REMEMBERED_MODE_KEY, mode);
  } catch (_) {
    /* a private-mode storage failure must not block the conversation */
  }
};

const DOMAIN_SHORT_CODE = {
  Identity: "ID",
  Cognition: "CO",
  Judgement: "JD",
  Judgment: "JD",
  Resolution: "RS",
  Motivation: "MO",
};

const ElementDetailModal = ({ isOpen, onClose, element, preloadedCards }) => {
  const navigate = useNavigate();

  const modalRef = useRef(null);
  const [animationStage, setAnimationStage] = useState("zoom-in");

  // Lazy AI card sections + mark-viewed
  const [showingUpRecently, setShowingUpRecently] = useState(null);
  const [tryDifferentApproach, setTryDifferentApproach] = useState(null);
  const [recentLoading, setRecentLoading] = useState(false);
  const [approachLoading, setApproachLoading] = useState(false);

  // Element conversation entry (PB-12): the mode question the endpoint asked,
  // and whether a call to it is in flight.
  const [modeChoice, setModeChoice] = useState(null);
  const [entering, setEntering] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setAnimationStage("zoom-in");
      setShowingUpRecently(null);
      setTryDifferentApproach(null);
      setModeChoice(null);
      setEntering(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !element) return;
    const elementKey = element.key || element.element_key || (element.symbol || "").toLowerCase();
    if (!elementKey) return;

    // The API may return the label as either lowercase ("recent"/"grow", as in
    // the v2 elements payload) or already-uppercased ("RECENT"/"GROW"). Normalize
    // before deciding which AI card sections to show.
    const normalizedLabel = String(element.label || "").toLowerCase();

    let cancelled = false;
    markElementViewed(elementKey).catch(() => {});

    if (normalizedLabel === "recent") {
      // Prefer the pre-rendered card from the elements payload — saves a round
      // trip and lets the card render instantly. Fall back to the per-element
      // endpoint only when the bulk payload didn't include it.
      const preRecent = preloadedCards?.showing_up_recently || null;
      if (preRecent && preRecent.content) {
        setShowingUpRecently(preRecent);
        setRecentLoading(false);
      } else {
        setRecentLoading(true);
        getShowingUpRecently(elementKey)
          .then((data) => { if (!cancelled) setShowingUpRecently(data || null); })
          .finally(() => { if (!cancelled) setRecentLoading(false); });
      }
    }

    if (normalizedLabel === "grow") {
      const preApproach = preloadedCards?.try_different_approach || null;
      if (preApproach && preApproach.content) {
        setTryDifferentApproach(preApproach);
        setApproachLoading(false);
      } else {
        setApproachLoading(true);
        getTryDifferentApproach(elementKey)
          .then((data) => { if (!cancelled) setTryDifferentApproach(data || null); })
          .finally(() => { if (!cancelled) setApproachLoading(false); });
      }
    }

    return () => { cancelled = true; };
  }, [isOpen, element, preloadedCards]);

  const handleClose = () => {
    setAnimationStage("zoom-out");
    setTimeout(() => { onClose(); }, 400);
  };

  // Per the Element Coaching & Roleplay contract, an element card always
  // launches a `scope=element` conversation with a `prompt_variant` that
  // mirrors the card label the member tapped (NEW / RECENT / GROW).
  // The synthesized first-message ("question") is shown as the user's
  // chat bubble and becomes the literal `query` the backend sees.
  const ELEMENT_PROMPT_VARIANT_FALLBACK = "new";
  const buildElementQuestion = (topic, variant) => {
    const t = topic || "this";
    switch (variant) {
      case "recent":
        return `Tell me how ${t} has been showing up for me`;
      case "grow":
        return `Help me work with my ${t} element`;
      case "new":
      default:
        return `Tell me about my ${t} element`;
    }
  };

  // Build a navigation URL that ChatInterface (chat) or Coach (voice)
  // consumes to dispatch the right Aris flow.
  const buildHandoffUrl = (action, modality) => {
    // Keyboard-mode chat goes straight to /chat so ChatInterface's
    // auto-start effect can fire on mount; voice goes to /coach/private.
    const basePath = modality === "voice" ? "/coach/private" : "/chat";
    const params = new URLSearchParams();
    params.set("element_action", action);
    params.set("modality", modality);
    if (element?.displayName) params.set("topic", element.displayName);

    const rawDomain = element?.category || element?.domain || "";
    const normalizedDomain = DOMAIN_KEY_NORMALIZE[rawDomain] || rawDomain;
    if (normalizedDomain) params.set("domain", normalizedDomain);
    if (element?.level) params.set("level", element.level);

    if (action === "coaching") {
      // Element-card coaching is always scope=element — single-element
      // conversation. prompt_variant mirrors the card label (NEW/RECENT/
      // GROW). question is the synthesized first user message.
      const variant = (element?.label || ELEMENT_PROMPT_VARIANT_FALLBACK).toLowerCase();
      params.set("scope", "element");
      params.set("prompt_variant", variant);
      const question = buildElementQuestion(element?.displayName, variant);
      params.set("question", question);
      // Existing chat pipeline detection flag.
      params.set("elements", "1");
    } else if (action === "roleplay") {
      const situation = tryDifferentApproach?.content || element?.content?.tryThisInstead || "";
      const reason = element?.content?.whyThisMatters || "";
      if (situation) params.set("situation", situation);
      if (reason) params.set("reason", reason);
      if (element?.relationship) params.set("relationship", element.relationship);
    }

    return `${basePath}?${params.toString()}`;
  };

  /**
   * PB-12 handoff URL, built from what the entry endpoint resolved.
   *
   * Same query contract as the legacy path — both consumers read a fixed set
   * of params — but the values are now the endpoint's: `topic` is the
   * canonical Element name it validated, `level` is the level it read from the
   * member's own record (rather than whatever the tile happened to carry), and
   * `element_opening` is the applied, member-specific opening it composed.
   */
  const buildEntryUrl = (entry, sessionId = null) => {
    const ctx = entry?.entry_context || {};
    const voice = entry?.mode === "VOICE";
    const basePath = voice ? "/coach/private" : "/chat";
    const variant = (element?.label || ELEMENT_PROMPT_VARIANT_FALLBACK).toLowerCase();
    const topic = ctx.selected_element || element?.displayName || "";

    const params = new URLSearchParams();
    params.set("element_action", "coaching");
    params.set("modality", voice ? "voice" : "chat");
    params.set("elements", "1");
    params.set("scope", "element");
    params.set("prompt_variant", variant);
    if (topic) params.set("topic", topic);

    const rawDomain = element?.category || element?.domain || "";
    const normalizedDomain = DOMAIN_KEY_NORMALIZE[rawDomain] || rawDomain;
    if (normalizedDomain) params.set("domain", normalizedDomain);

    const level = ctx.element_level || element?.level;
    if (level) params.set("level", level);

    if (voice) {
      // The voice WebSocket client whitelists the query params it forwards
      // (Components/realtime-api-beta — vendored, not ours to edit), so the
      // opening cannot travel this way; the voice runtime composes its own
      // from the same scope=element context. `question` stays what the
      // Element Coaching contract already defines it as — the member-side
      // trigger phrase, not displayed in a voice session.
      params.set("question", buildElementQuestion(topic, variant));
    } else if (entry?.opening) {
      // Text: Aris speaks first, with the opening the endpoint composed. The
      // param renders it immediately; the session carries the copy the model
      // will actually read, which aris-ai stored against this conversation.
      params.set("element_opening", entry.opening);
      if (sessionId) params.set("chat_session_id", sessionId);
    }

    return `${basePath}?${params.toString()}`;
  };

  // Fallback only: the pre-PB-12 behaviour (read localStorage, launch straight
  // away). Used for roleplay, which the entry endpoint does not cover, and
  // when the endpoint itself is unreachable — a member who taps a CTA should
  // still get a conversation.
  const launchAction = (action) => {
    const modality = getCoachingMode() === "voice" ? "voice" : "chat";
    const url = buildHandoffUrl(action, modality);
    onClose();
    navigate(url);
  };

  /**
   * "Talk with Aris" — a choice, not a launch.
   *
   * Foundational Self-Knowledge v26.1, "Conversation Entry": selecting this
   * does NOT immediately launch a conversation; Aris asks how the member would
   * like to continue, and the answer is remembered. The endpoint built for it
   * (`POST /ARIS/v4/discovery/elements/{name}/talk`) owns both halves — the
   * question, and the entry context + applied opening behind it.
   */
  const openElementConversation = async () => {
    const name = element?.displayName;
    if (!name) {
      launchAction("coaching");
      return;
    }
    setEntering(true);
    try {
      const entry = await enterElementConversation(name);
      if (entry?.requires_mode_choice) {
        setModeChoice(entry);
        return;
      }
      if (entry?.mode) {
        // The endpoint resolved without asking. Honour it rather than
        // second-guessing which half of the contract it meant.
        onClose();
        navigate(buildEntryUrl(entry));
        return;
      }
      launchAction("coaching");
    } catch (_) {
      launchAction("coaching");
    } finally {
      setEntering(false);
    }
  };

  const chooseMode = async (mode) => {
    const name = element?.displayName;
    setEntering(true);
    try {
      // Text only: the chat session has to exist *before* the entry call, because
      // that is what the opening gets stored against. Conversation history is
      // server-side, so an opening this client renders but never persists leaves
      // the model reading the member's reply as the conversation's opening line.
      // Voice composes its own opening in its own runtime and needs none of this.
      //
      // Minting it here costs nothing: `initiateChatSession` is a local uuid, not
      // a round trip, so an abandoned choice leaves no server-side trace.
      let sessionId = null;
      if (String(mode).toUpperCase() === "TEXT") {
        const session = await initiateChatSession();
        sessionId = session?.payload?.chat_id ?? null;
      }

      const entry = await enterElementConversation(name, {
        mode,
        rememberMode: true,
        sessionId,
      });
      if (entry?.mode) {
        rememberMode(entry.mode);
        onClose();
        navigate(buildEntryUrl(entry, sessionId));
        return;
      }
      launchAction("coaching");
    } catch (_) {
      launchAction("coaching");
    } finally {
      setEntering(false);
    }
  };

  if (!isOpen || !element) return null;

  const rawDomain = element?.category || element?.domain || "";
  const domainShort = DOMAIN_SHORT_CODE[rawDomain] || (element?.symbol || "").slice(0, 2).toUpperCase();
  const paddedId = String(element?.id ?? "").padStart(2, "0");

  // Pull Strength + Watch For (Potential Risk) out of `levelDescription`'s
  // single HTML blob. Prefer the level-specific lead paragraph for "How
  // This Shows Up" — it's the user-personalized text. Fall back to the
  // generic `definition` only when the level intro isn't there.
  const parsed = parseLevelDescription(element?.content?.levelDescription);
  const howThisShowsUp =
    element?.content?.howThisShowsUp ||
    parsed.howThisShowsUp ||
    element?.content?.definition ||
    "";
  const strength =
    element?.content?.strength ||
    element?.content?.strengths ||
    parsed.strength ||
    "";
  const watchFor =
    element?.content?.watchFor ||
    element?.content?.watchOut ||
    parsed.watchFor ||
    "";

  const label = String(element?.label || "").toLowerCase();
  const isGrow = label === "grow";
  const isRecent = label === "recent";

  const levelLabel = element?.level || "";

  // Which option the chooser highlights: the member's last choice if they have
  // one, otherwise whichever the endpoint flags as recommended.
  const rememberedMode = getRememberedMode();
  const defaultMode = (modeChoice?.options || []).find(
    (option) => option.recommended
  )?.mode;

  return (
    <div className="element-detail-overlay">
      <div
        ref={modalRef}
        className={`element-detail-modal ${animationStage}`}
      >
        <button
          onClick={handleClose}
          className="element-detail-close"
          aria-label="Close"
        >
          <XGreyIcon className="element-detail-close-icon" aria-hidden="true" />
        </button>

        <div className="element-detail-scroll">
          {/* Hero: black card with ID badge, gem, name + level — matches
              Figma node 14863:77892 (Element Info Card – Default). */}
          <div className="element-hero">
            <div className="element-hero-id">
              <span className="element-hero-id-prefix">{domainShort}</span>
              <span className="element-hero-id-bullet">•</span>
              <span className="element-hero-id-value">{paddedId}</span>
            </div>
            <div className="element-hero-image">
              <img src={element.element_url} alt={element.displayName || element.symbol || ""} />
            </div>
            <h2 className="element-hero-name">{element.displayName}</h2>
            {levelLabel && <p className="element-hero-level">{levelLabel}</p>}
          </div>

          {/* Three default sections */}
          {howThisShowsUp && (
            <div className="element-info-section">
              <h3 className="element-info-section-title">How This Shows Up</h3>
              <p
                className="element-info-section-body"
                dangerouslySetInnerHTML={{ __html: howThisShowsUp }}
              />
            </div>
          )}
          {strength && (
            <>
              <div className="element-info-divider" />
              <div className="element-info-section">
                <h3 className="element-info-section-title">Strength</h3>
                <p
                  className="element-info-section-body"
                  dangerouslySetInnerHTML={{ __html: strength }}
                />
              </div>
            </>
          )}
          {watchFor && (
            <>
              <div className="element-info-divider" />
              <div className="element-info-section">
                <h3 className="element-info-section-title">Watch For</h3>
                <p
                  className="element-info-section-body"
                  dangerouslySetInnerHTML={{ __html: watchFor }}
                />
              </div>
            </>
          )}

          {/* RECENT-only: Showing Up Recently (blue card)
              Matches Figma 14914:17519 styling, recent variant. */}
          {isRecent && (recentLoading || (showingUpRecently && showingUpRecently.content)) && (
            <div className="element-ai-card element-ai-card-recent">
              {recentLoading ? (
                <>
                  <h3 className="element-ai-card-title">Showing Up Recently</h3>
                  <div className="element-ai-skeleton">
                    <span /><span /><span />
                  </div>
                </>
              ) : (
                parseCardContent(showingUpRecently.content).map((section, idx) => (
                  <div key={`recent-${idx}`} className="element-ai-card-section">
                    <h3 className="element-ai-card-title">{section.title}</h3>
                    {section.items.map((item, j) => {
                      if (item.kind === "intro") {
                        return (
                          <p key={j} className="element-ai-card-intro">{item.text}</p>
                        );
                      }
                      if (item.kind === "list") {
                        return (
                          <div key={j} className="element-ai-card-list">
                            {item.lines.map((line, k) => (
                              <p key={k} className="element-ai-card-line">{line}</p>
                            ))}
                          </div>
                        );
                      }
                      return (
                        <p key={j} className="element-ai-card-line">{item.text}</p>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          )}

          {/* GROW-only: Try a Different Approach (green card)
              Matches Figma node 14914:17519 — bg rgba(0,187,155,0.02),
              1px #00bb9b border, centered sections with green titles. */}
          {isGrow && (approachLoading ||
            (tryDifferentApproach && tryDifferentApproach.eligible !== false && tryDifferentApproach.content)) && (
            <div className="element-ai-card element-ai-card-grow">
              {approachLoading ? (
                <>
                  <h3 className="element-ai-card-title">Try a Different Approach</h3>
                  <div className="element-ai-skeleton">
                    <span /><span /><span />
                  </div>
                </>
              ) : (
                parseCardContent(tryDifferentApproach.content).map((section, idx) => (
                  <div key={`grow-${idx}`} className="element-ai-card-section">
                    <h3 className="element-ai-card-title">{section.title}</h3>
                    {section.items.map((item, j) => {
                      if (item.kind === "intro") {
                        return (
                          <p key={j} className="element-ai-card-intro">{item.text}</p>
                        );
                      }
                      if (item.kind === "list") {
                        return (
                          <div key={j} className="element-ai-card-list">
                            {item.lines.map((line, k) => (
                              <p key={k} className="element-ai-card-line">{line}</p>
                            ))}
                          </div>
                        );
                      }
                      return (
                        <p key={j} className="element-ai-card-line">{item.text}</p>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Footer CTAs (Figma node 14914:17428).
            For GROW elements there are two stacked CTAs, each with its own
            helper line: a white "Practice this Scenario" button followed by
            the blue "Talk it through with Aris" primary CTA. For other
            elements only the primary CTA is shown.
            When the user is in voice coaching mode, the primary CTA swaps
            its icon to the audio-waveform glyph. */}
        <div className="element-detail-footer element-detail-footer-ctas">
          {modeChoice ? (
            /* Conversation Entry (Foundational Self-Knowledge v26.1): Aris
               asks how to continue before anything launches. Options, their
               order and the recommendation come from the endpoint. */
            <div className="element-mode-choice">
              <p className="element-mode-choice__question">
                {tk(modeChoice.question_key)}
              </p>
              {(modeChoice.options || []).map((option) => {
                const preferred =
                  option.mode === (rememberedMode || defaultMode);
                return (
                  <button
                    key={option.mode}
                    type="button"
                    className={
                      "element-mode-option" +
                      (preferred ? " element-mode-option--preferred" : "")
                    }
                    onClick={() => chooseMode(option.mode)}
                    disabled={entering}
                  >
                    <span className="element-mode-option__label">
                      {tk(option.label_key)}
                    </span>
                    {MODE_DESCRIPTION[option.mode] && (
                      <span className="element-mode-option__desc">
                        {MODE_DESCRIPTION[option.mode]}
                      </span>
                    )}
                    {option.hint_key && (
                      <span className="element-mode-option__hint">
                        {tk(option.hint_key)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <>
              {isGrow && (
                <div className="element-cta-block">
                  <button
                    type="button"
                    className="element-cta element-cta-secondary"
                    onClick={() => launchAction("roleplay")}
                  >
                    Practice this Scenario
                  </button>
                  <p className="element-detail-cta-helper">
                    Try a different approach in a real situation.
                  </p>
                </div>
              )}
              <div className="element-cta-block">
                <button
                  type="button"
                  className="element-cta element-cta-primary"
                  onClick={openElementConversation}
                  disabled={entering}
                >
                  <AudioAiIcon className="element-cta-icon" aria-hidden="true" />
                  Talk it through with Aris
                </button>
                <p className="element-detail-cta-helper">
                  Get a clearer sense of how this shows up for you.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ElementDetailModal;
